// Шпаргалка изотерм: ответ ручки → строки таблицы «вид × глубина».
//
// Логика вынесена из вёрстки ради тестов: ячейка обязана говорить ровно то, что
// делает модель. У вида с глубинной моделью диапазон `sst` один на все
// горизонты — и ячейка одна на 0 и 30 м, с оговоркой. У поверхностного вида на
// 30 м температура воды модели не видна (или видна слоем 0–50 м) — там своя
// ячейка и своя причина. Тесты проверяют КЛЮЧИ каталога, а не формулировки.
import type { ThermalBand, ThermalHint, ThermalHintsResponse } from '../api'
import { ISOTHERM_STEP } from '../map/isotherms'

export interface CheatCell {
  /** Горизонты, которые покрывает ячейка: [0, 30] — одна на обе. */
  depths: number[]
  /** Диапазон модели; null — на этих глубинах модель температуру не сравнивает. */
  band: ThermalBand | null
  /** Подпись источника, если это не `sst` (слой 0–50 м). */
  sourceKey: string | null
  /** Почему диапазона нет. */
  reasonKey: string | null
}

export interface CheatRow {
  speciesId: number
  species: { name: string; name_en?: string | null }
  selected: boolean
  /** Месячной строки нет — действует круглогодичная модель. */
  yearRound: boolean
  cells: CheatCell[]
  /** Оговорка к строке («один диапазон на все глубины»). */
  noteKey: string | null
}

function bandOf(h: ThermalHint, range: ThermalHint['depths'][number]['range']): ThermalBand | null {
  if (range == null) return null
  if (range === 'sst') return h.sst
  return h.layers[range] ?? null
}

function rowOf(h: ThermalHint, selected: number | null): CheatRow {
  // Соседние горизонты с одним и тем же ответом сливаются в одну ячейку:
  // повторять одно число дважды значило бы намекать, что это два разных.
  const cells: (CheatCell & { key: string })[] = []
  for (const d of h.depths) {
    const key = `${d.range ?? ''}|${d.reason ?? ''}`
    const last = cells[cells.length - 1]
    if (last && last.key === key) {
      last.depths.push(d.depth)
      continue
    }
    cells.push({
      key,
      depths: [d.depth],
      band: bandOf(h, d.range),
      sourceKey: d.range && d.range !== 'sst' ? `maps.isotherm.cheat.src.${d.range}` : null,
      reasonKey: d.reason ? `maps.isotherm.cheat.reason.${d.reason}` : null,
    })
  }
  const merged = cells.length === 1 && cells[0].band != null
  return {
    speciesId: h.species_id,
    species: { name: h.name, name_en: h.name_en },
    selected: selected === h.species_id,
    yearRound: h.model_month == null,
    cells: cells.map(({ key: _key, ...c }) => c),
    noteKey:
      merged && h.same_range_all_depths && h.depths.length > 1
        ? 'maps.isotherm.cheat.note.sameAllDepths'
        : null,
  }
}

export function cheatRows(resp: ThermalHintsResponse | undefined, selected: number | null): CheatRow[] {
  return (resp?.species ?? []).map(h => rowOf(h, selected))
}

/** Подпись горизонтов ячейки без единицы: «0», «30», «0 / 30».
 *
 *  Глубину называет САМА ячейка, а не шапка колонок: в панели 256 px колонка
 *  под ряд диапазона («лучше 14,0 – 16,0») вдвое уже нужного, и ячейки ехали
 *  друг по другу. Ячейки идут теперь одна под другой, и шапке не над чем
 *  стоять. */
export function depthLabel(depths: number[]): string {
  return depths.join(' / ')
}

/** Край диапазона → значение основной изотермы: на шаг ползунка и в его
 *  границы. null остаётся null — отсутствующий край не становится нулём. */
export function isoPick(v: number | null, min: number, max: number): number | null {
  if (v == null || !Number.isFinite(v)) return null
  const snapped = Math.round(v / ISOTHERM_STEP) * ISOTHERM_STEP
  return Math.min(max, Math.max(min, snapped))
}
